import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { useRef, type FC, type MouseEventHandler, type ReactNode } from 'react';
import Column from 'src/components-v2/Layout/Grid/Column';
import Stack from 'src/components-v2/Layout/Stack/Stack';
import type { MaybeBackgroundContainerAugmentedEntry } from 'src/components-v3/BackgroundContainer/BackgroundContainer';
import DecorativeStat from 'src/components-v3/DecorativeStat/DecorativeStat';
import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import Wrapper from 'src/containers/Wrapper/Wrapper';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import { targetMap } from 'src/lib/constants';
import { makeImageQueryString, makeImageSource } from 'src/lib/utils';
import type {
  AssetEntry,
  TestimonialSectionEntry,
  TestimonialSectionShape,
} from 'src/types/contentful';
import SxProps from 'threads5/themes/sx-props';
import { ITheme } from 'threads5/themes/theme';
import ThemeProvider from 'threads5/themes/theme-provider';
import {
  StyledCard,
  StyledCardBackgroundImage,
  StyledCardBackgroundImageWrapper,
  StyledCardCtaRegion,
  StyledCta,
  StyledCtaWrapper,
  StyledFullWidth,
  StyledFullWidthBackgroundImage,
  StyledQuote,
} from './TestimonialSection.styled';

const FullWidthBackgroundImage: FC<{
  asset: AssetEntry;
  sx: Record<string, unknown>;
}> = ({ asset, sx }) => {
  const { lgImgSrc, mdImgSrc, smImgSrc } = makeImageSource(asset.fields);

  return (
    <StyledFullWidthBackgroundImage
      asset={asset.fields}
      sys={asset.sys}
      sx={sx}
      sources={{
        // Full-width, up to 70% height
        xs: makeImageQueryString({ src: smImgSrc, width: 640 }), // 640 * 1

        // Full-height, up to 70% width
        sm: makeImageQueryString({ src: mdImgSrc, width: 716 }), // 1024 * .7
        md: makeImageQueryString({ src: mdImgSrc, width: 896 }), // 1280 * .7
        lg: makeImageQueryString({ src: lgImgSrc, width: 1008 }), // 1440 * .7
        xl: makeImageQueryString({ src: lgImgSrc, width: 1211 }), // 1730 * .7
      }}
    />
  );
};

const CardBackgroundImage: FC<{
  asset: AssetEntry;
}> = ({ asset }) => {
  const { lgImgSrc, mdImgSrc, smImgSrc } = makeImageSource(asset.fields);

  // As of 2025-12-09 in Chrome, anything < 100 causes artifacts to appear
  // when preset images are scaled down. This does not seem to affect
  // full-width as we do not scale those images nearly as much.
  const quality = 100;

  return (
    <StyledCardBackgroundImageWrapper className='testimonial-section-card-background-image-wrapper'>
      <StyledCardBackgroundImage
        asset={asset.fields}
        sys={asset.sys}
        sources={{
          // Dimensions measured in DOM and are a bit arbitary due to flexible container padding
          xs: makeImageQueryString({ src: smImgSrc, width: 614, quality }),
          sm: makeImageQueryString({ src: mdImgSrc, width: 944, quality }),
          md: makeImageQueryString({ src: mdImgSrc, width: 1200, quality }),
          lg: makeImageQueryString({ src: lgImgSrc, width: 1130, quality }),
          xl: makeImageQueryString({ src: lgImgSrc, width: 1372, quality }),
        }}
      />
    </StyledCardBackgroundImageWrapper>
  );
};

const Quote: FC<{
  body: TestimonialSectionShape['body'];
  sys: TestimonialSectionEntry['sys'];
  appearance: TestimonialSectionShape['appearance'];
}> = ({ body, sys, appearance }) => {
  return (
    <StyledQuote appearance={appearance} gap={10}>
      <RichTextSwitch
        sys={sys}
        content={body}
        nodes={{
          h3: {
            fieldId: 'body',
            variant: 'Medium Headline',
            theme: 'web 3.0',
          },
          p: {
            sx: {
              ':last-of-type:empty': {
                display: 'none',
              },
            },
          },
        }}
      />
    </StyledQuote>
  );
};

const Stats: FC<{
  decorativeStats: Required<TestimonialSectionShape['decorativeStats']>;
}> = ({ decorativeStats }) => {
  return (
    <Stack
      flexDirection={{ xs: 'column', sm: 'row' }}
      gap={{ xs: 4, sm: 9.625, md: 12.25 }}
      position='relative'
      zIndex={1}
    >
      {decorativeStats.map((stat) => {
        return (
          <DecorativeStat
            key={stat.sys.id}
            layout='Left'
            appearance='Small'
            {...stat}
          />
        );
      })}
    </Stack>
  );
};

const Cta: FC<TestimonialSectionShape['cta']> = ({
  fields: { url, trackingId, target, text, styles },
  sys,
}) => {
  return (
    // Prevent clicks from bubbling up to parent wrapper which may proxy clicks back to this element.
    // This is needed as we are unable to easily expand the clickable area of the shared
    // button component because of the way it's built.
    <StyledCtaWrapper
      onClick={(event) => {
        return event.stopPropagation();
      }}
    >
      <StyledCta
        variant='primary'
        href={url}
        sx={styles}
        sys={sys}
        target={targetMap[target]}
        trackingId={trackingId}
      >
        {text}
      </StyledCta>
    </StyledCtaWrapper>
  );
};

type AppearanceWrapperProps = {
  background?: string;
  backgroundImages?: ReactNode;
  children: ReactNode;
  className?: string;
  hasCta: boolean;
  sx?: SxProps<ITheme>;
};

const CardAppearanceWrapper: FC<AppearanceWrapperProps> = ({
  children,
  backgroundImages,
  hasCta,
  ...rest
}) => {
  const card = useRef<HTMLDivElement>(null);

  /**
   * Expand clickable region of CTA to entire card. We would normally accomplish this elegantly
   * through pseudo elements, but the entirety of our CTA component is `position: relative`,
   * so this can't be easily done without restructuring that component.
   */
  const proxyClickToCta: MouseEventHandler<HTMLDivElement> = (event) => {
    const ctaDom = card.current?.querySelector('a');
    if (!ctaDom) {
      return;
    }

    event.preventDefault();
    ctaDom.click();
  };

  return (
    // Because we only apply `sx` at the child card container, this element
    // is only selectable through `sx` via a parent wrapper container.
    <Wrapper className='cms-testimonial-section-card-wrapper'>
      <Column xs={24} lg={20} lgOffset={2}>
        <StyledCard {...rest} ref={card} hasCta={hasCta}>
          {hasCta && <StyledCardCtaRegion onClick={proxyClickToCta} />}
          {backgroundImages}
          {children}
        </StyledCard>
      </Column>
    </Wrapper>
  );
};

const FullWidthAppearanceWrapper: FC<AppearanceWrapperProps> = ({
  children,
  backgroundImages,
  hasCta,
  ...rest
}) => {
  return (
    <StyledFullWidth {...rest}>
      {backgroundImages}
      <Column xs={24} lg={20} lgOffset={2}>
        {children}
      </Column>
    </StyledFullWidth>
  );
};

const AppearanceWrapper: FC<
  AppearanceWrapperProps & { appearance: TestimonialSectionShape['appearance'] }
> = ({ appearance, ...rest }) => {
  return appearance === 'Card' ? (
    <CardAppearanceWrapper {...rest} />
  ) : (
    <FullWidthAppearanceWrapper {...rest} />
  );
};

export const TestimonialSection: FC<
  MaybeBackgroundContainerAugmentedEntry<TestimonialSectionEntry>
> = ({ fields, sys }) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys.id,
  });

  const untamperedBackgroundColor =
    'intrinsicBackgroundColor' in fields
      ? fields.intrinsicBackgroundColor
      : fields.backgroundColor;

  const untamperedMode =
    'intrinsicMode' in fields ? fields.intrinsicMode : fields.mode;

  const componentTheme = useComponentTheme(untamperedMode);
  const lightModeTheme = useComponentTheme('light');

  return (
    <ThemeProvider theme={componentTheme}>
      <AppearanceWrapper
        {...inspectorProps({ fieldId: 'internalTitle' })}
        appearance={fields.appearance}
        className='cms-testimonial-section'
        background={
          untamperedBackgroundColor?.fields
            ? lightModeTheme.palette[
                'color' in untamperedBackgroundColor.fields
                  ? untamperedBackgroundColor.fields.color
                  : `gradient/${untamperedBackgroundColor.fields.gradient}`
              ]
            : undefined
        }
        sx={fields.styles}
        hasCta={!!fields.cta}
        backgroundImages={
          <>
            {fields.appearance === 'Full Width' && (
              <>
                {'fullWidthBackgroundImage1' in fields &&
                  fields.fullWidthBackgroundImage1 && (
                    <FullWidthBackgroundImage
                      asset={fields.fullWidthBackgroundImage1}
                      sx={{ left: 0, top: 0, objectPosition: 'bottom right' }}
                    />
                  )}
                {'fullWidthBackgroundImage2' in fields &&
                  fields.fullWidthBackgroundImage2 && (
                    <FullWidthBackgroundImage
                      asset={fields.fullWidthBackgroundImage2}
                      sx={{
                        right: 0,
                        bottom: 0,
                        objectPosition: 'top left',
                      }}
                    />
                  )}
              </>
            )}

            {fields.appearance === 'Card' &&
              'cardBackgroundImage' in fields &&
              fields.cardBackgroundImage && (
                <CardBackgroundImage asset={fields.cardBackgroundImage} />
              )}
          </>
        }
      >
        <Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 6, md: 0 }}>
          <Stack flexGrow={1} gap={{ xs: 4, md: 8 }}>
            <Quote
              body={fields.body}
              sys={sys}
              appearance={fields.appearance}
            />

            {fields.decorativeStats?.length && (
              <Stats
                {...inspectorProps({ fieldId: 'stats' })}
                decorativeStats={fields.decorativeStats}
              />
            )}
          </Stack>

          {fields.cta && (
            <Stack
              alignItems={{ xs: 'flex-start', md: 'flex-end' }}
              justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              flexShrink={0}
            >
              <Cta {...inspectorProps({ fieldId: 'cta' })} {...fields.cta} />
            </Stack>
          )}
        </Stack>
      </AppearanceWrapper>
    </ThemeProvider>
  );
};

export default TestimonialSection;
